<!-- Figma component: Sidebar/Entity -->
<script setup lang="ts">
import { isFileField, isFileProperty } from '@/shared/utils/typeGuards'
import { computed, ref } from 'vue'
import type { Field } from './Fields/types'
import { isGroundableField } from './Fields/utils/grounding'
import type { Property } from './Properties/types'
import { useEntitySidebarStore } from './useEntitySidebarStore'
import { usePrevOrNextEntity } from './usePrevOrNextEntity'
import { useProject } from './useProject'
import { useSerializeFieldToText } from './useSerializeFieldToText'

import { getEntityFieldSidebar } from '@/modules/Project/Fields/field-registry'
import { useFieldSaving } from '@/modules/Project/useFieldSaving'
import { invariant } from '@/shared/utils/typeAssertions'
import CrumbChevron from '@/sharedComponents/CrumbChevron.vue'
import EntitySidebarLayout from '@/sharedComponents/EntitySidebarLayout.vue'
import SidebarHeaderLayout from '@/sharedComponents/SidebarHeaderLayout.vue'
import SidebarJumpToProperty, {
  jumpToFieldAttrs,
} from '@/sharedComponents/SidebarJumpToProperty.vue'
import CircularProgress from '@/uiKit/CircularProgress.vue'
import IconButton from '@/uiKit/IconButton.vue'
import EntityCrumb from '../Workspaces/Crumbs/EntityCrumb.vue'
import { useFieldDeleteFile } from './useFieldDeleteFile'
import { useGroundingStore, type ClaimAndSource } from './useGroundingStore'
import { useOpenGrounding } from './useOpenGrounding'

defineProps<{ workspaceId: string; projectId: string }>()
defineEmits<{ close: [] }>()

const entitySidebarStore = useEntitySidebarStore()
const projectStore = useProject()
const { saveFieldChange } = useFieldSaving()
const serialize = useSerializeFieldToText()
const { previousEntityId, nextEntityId, loadAndSetEntity } = usePrevOrNextEntity()

const container = ref<HTMLDivElement>()

const fields = computed(() => {
  const entity = entitySidebarStore.selectedEntity
  if (!entity) return []

  return projectStore.visibleProperties
    .map((property) => {
      const field = entity.fields.get(property.id)
      if (!field) return null

      return {
        property,
        field,
        serializedValue: serialize(field),
      }
    })
    .filter(
      (item): item is { property: Property; field: Field; serializedValue: string } =>
        item !== null,
    )
})

const groundingStore = useGroundingStore()
const { openGrounding } = useOpenGrounding()

const onClickClaim = async (field: Field, { claimId, sourceId }: ClaimAndSource) => {
  if (!isGroundableField(field) || !field.hasGrounding || !field.grounding) {
    return
  }

  // Find the file field that corresponds to this source
  const fileField = fields.value.find((f) => isFileField(f.field) && isFileProperty(f.property))

  if (fileField && fileField.field.type === 'file') {
    entitySidebarStore.setSelectedFileField(fileField.field, field)
    await openGrounding({ propertyId: field.propertyId, entityId: field.entityId })
    groundingStore.clickClaim({ claimId, sourceId })
  }
}

const onPrevEntity = async () => {
  if (!previousEntityId.value) return
  const prevId = previousEntityId.value
  entitySidebarStore.setSelectedEntityId(prevId)
  await loadAndSetEntity(prevId)
}

const onNextEntity = async () => {
  if (!nextEntityId.value) return
  const nextId = nextEntityId.value
  entitySidebarStore.setSelectedEntityId(nextId)
  await loadAndSetEntity(nextId)
}

const { deleteFile } = useFieldDeleteFile()

function onDeleteFile(field: Field<'file' | 'file_collection'>) {
  const entity = projectStore.activeView?.entities?.find((e) => e?.id === field.entityId)
  invariant(entity)
  deleteFile(field, entity.parentEntityId)
}
</script>

<template>
  <div
    ref="container"
    class="z-0 mb-2.5 flex size-full flex-col overflow-hidden bg-surface-primary p-0"
    data-test="entity-sidebar"
  >
    <!-- Resize handle -->

    <SidebarHeaderLayout class="border-b border-border-subtle">
      <template
        v-if="entitySidebarStore.selectedEntity"
        #breadcrumbs
      >
        <EntityCrumb
          :entity="entitySidebarStore.selectedEntity"
          mask
        />
        <CrumbChevron />
        <SidebarJumpToProperty :properties="fields.map((f) => f.property)" />
      </template>
      <template
        v-else
        #breadcrumbs
      >
        <CircularProgress
          role="progressbar"
          aria-valuemin="0"
          aria-valuemax="100"
          aria-label="Loading entity"
          size="sm"
        />
      </template>
      <template #actions>
        <div class="flex items-center gap-1">
          <IconButton
            size="md"
            variant="transparent"
            icon="chevron-top"
            aria-label="Previous entity"
            :disabled="!previousEntityId"
            @click="onPrevEntity"
          />
          <IconButton
            size="md"
            variant="transparent"
            icon="chevron-bottom"
            aria-label="Next entity"
            :disabled="!nextEntityId"
            @click="onNextEntity"
          />
          <IconButton
            size="md"
            variant="transparent"
            icon="close"
            aria-label="Close sidebar"
            @click="$emit('close')"
          />
        </div>
      </template>
    </SidebarHeaderLayout>
    <div
      class="flex grow flex-col overflow-auto scrollbar-thin scrollbar-track-background-transparent scrollbar-thumb-background-gray-subtle scrollbar-track-rounded-md [contain:strict]"
    >
      <EntitySidebarLayout>
        <component
          :is="getEntityFieldSidebar(field)"
          v-for="{ field, property } in fields"
          :key="property.id"
          ref="fieldRefs"
          :field="field"
          :property="property"
          v-bind="jumpToFieldAttrs(property.id)"
          @submit="saveFieldChange(field, $event)"
          @show-source="onClickClaim(field, $event)"
          @file:delete="onDeleteFile"
          @file:preview="entitySidebarStore.setSelectedFileField"
        />
      </EntitySidebarLayout>
    </div>
  </div>
</template>
